import { RefinementCtx } from 'zod';

import {
  CohortBuilderDefinitionLegacy,
  DerivedEventLegacy,
  DerivedEventTypeLegacy,
  SimpleEventLegacy,
} from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { ValidationCodes } from './common';
import { validate as validateTimeWindow } from './timeWindowValidations';

// Utils ========
// Duplicate method, already present in projects/feature-cohort-builder/src/utils/event-utils.ts
// cant use to avoid circular dependency
const extractEventName = (rawEventName?: string): string | undefined =>
  rawEventName?.split('::')?.[1]?.replace(/^"|"$/g, '');

// Simple event validations ========
const checkForSimpleEventFilters = (events: SimpleEventLegacy[], ctx: RefinementCtx) => {
  events.forEach((event, index) => {
    const hasConceptFilters = event.conceptFilters?.length > 0;
    const hasRelatedEntityFilters = event.relatedEntityFilters?.length > 0;
    const hasNumericFilters = event.numericFilters?.length > 0;
    const hasUniquenessFilters = event.uniquenessFilters?.length > 0;

    if (
      !(hasConceptFilters || hasRelatedEntityFilters || hasNumericFilters || hasUniquenessFilters)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['simpleEvents', index],
        params: {
          customCode: ValidationCodes.NO_FILTERS,
        },
      });
    }
  });
};

const validateSimpleEvents = (simpleEvents: SimpleEventLegacy[], ctx: RefinementCtx) => {
  checkForSimpleEventFilters(simpleEvents, ctx);
};

// Derived event validations ========
const checkForDerivedEventFilters = (events: DerivedEventLegacy[], ctx: RefinementCtx) => {
  events.forEach((event, index) => {
    const eventFiltersLength = event.eventFilters?.length;
    if (
      eventFiltersLength < 1 ||
      (event.type === DerivedEventTypeLegacy.SEQUENCE && eventFiltersLength < 2) ||
      (event.type === DerivedEventTypeLegacy.MERGED && eventFiltersLength < 2)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['derivedEvents', index],
        params: {
          customCode: ValidationCodes.NO_FILTERS,
        },
      });
    }
  });
};

const checkForMissingEventsForDerivedEvent = (
  cohortDefinition: CohortBuilderDefinitionLegacy,
  ctx: RefinementCtx
) => {
  const { simpleEvents, derivedEvents } = cohortDefinition;
  const allEventNames = [...simpleEvents, ...derivedEvents].map((event) => event.name);

  derivedEvents?.forEach((derivedEvent) => {
    derivedEvent?.eventFilters?.forEach((eventFilter, index) => {
      const eventName = extractEventName(eventFilter?.eventName?.value);
      if (!allEventNames.includes(eventName || '')) {
        ctx.addIssue({
          code: 'custom',
          path: ['derivedEvents', index],
          params: {
            customCode: ValidationCodes.NO_EVENT_NAME,
          },
        });
      }
    });
  });
};

const validateDerivedEvents = (
  cohortDefinition: CohortBuilderDefinitionLegacy,
  ctx: RefinementCtx
) => {
  checkForDerivedEventFilters(cohortDefinition.derivedEvents, ctx);
  checkForMissingEventsForDerivedEvent(cohortDefinition, ctx);
};

// Subject variable validations ========
const checkForCriteriaFilters = (
  cohortDefinition: CohortBuilderDefinitionLegacy,
  ctx: RefinementCtx
) => {
  const { subjectVariables } = cohortDefinition;
  subjectVariables?.forEach((subjectVariable, index) => {
    const hasConceptFilters = subjectVariable?.conceptFilters?.length > 0;
    const hasNumericFilters = subjectVariable?.numericFilters?.length > 0;
    const hasCountFilter =
      subjectVariable?.countFilter && Object.values(subjectVariable?.countFilter).length > 0;
    if (!hasConceptFilters && !hasNumericFilters && !hasCountFilter) {
      ctx.addIssue({
        code: 'custom',
        path: ['subjectVariables', index],
        params: {
          customCode: ValidationCodes.NO_FILTERS,
        },
      });
    }
  });
};

const checkForMissingEventsForCriteria = (
  cohortDefinition: CohortBuilderDefinitionLegacy,
  ctx: RefinementCtx
) => {
  const { simpleEvents, derivedEvents, subjectVariables } = cohortDefinition;
  const allEventNames = [...simpleEvents, ...derivedEvents].map((event) => event.name);

  subjectVariables?.forEach((subjectVariable, index) => {
    const inputField = subjectVariable?.countFilter?.inputFields?.[0];
    if (inputField) {
      const eventName = extractEventName(inputField?.value);
      if (!allEventNames.includes(eventName || '')) {
        ctx.addIssue({
          code: 'custom',
          path: ['subjectVariables', index],
          params: {
            customCode: ValidationCodes.NO_EVENT_NAME,
          },
        });
      }
    }
  });
};

const validateCriteria = (cohortDefinition: CohortBuilderDefinitionLegacy, ctx: RefinementCtx) => {
  checkForCriteriaFilters(cohortDefinition, ctx);
  checkForMissingEventsForCriteria(cohortDefinition, ctx);
  validateTimeWindow(cohortDefinition, ctx);
};

const validateForDuplicateNames = (
  cohortDefinition: CohortBuilderDefinitionLegacy,
  ctx: RefinementCtx
) => {
  const { simpleEvents, derivedEvents, subjectVariables } = cohortDefinition;
  const mapping: Record<string, Array<string | number>> = {};
  [
    { list: simpleEvents, key: 'simpleEvents' },
    { list: derivedEvents, key: 'derivedEvents' },
    { list: subjectVariables, key: 'subjectVariables' },
  ].forEach(({ list, key }) => {
    list?.forEach((item, index) => {
      const name = item?.name;
      const path = [key, index];
      if (name && !mapping[name]) {
        mapping[name] = path;
      } else if (mapping[name]) {
        ctx.addIssue({
          code: 'custom',
          path,
          params: { customCode: ValidationCodes.DUPLICATE_NAME },
        });
        ctx.addIssue({
          code: 'custom',
          path: mapping[name],
          params: { customCode: ValidationCodes.DUPLICATE_NAME },
        });
      }
    });
  });
};

export const validations = () => {
  return (cohortDefinition: CohortBuilderDefinitionLegacy, ctx: RefinementCtx) => {
    validateSimpleEvents(cohortDefinition.simpleEvents, ctx);
    validateDerivedEvents(cohortDefinition, ctx);
    validateCriteria(cohortDefinition, ctx);
    validateForDuplicateNames(cohortDefinition, ctx);
  };
};
export { ValidationCodes };
